import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PiInputfield from "../../components/PiInputField";
import PiTextArea from "../../components/PiTextArea";
import { selectAvailableLanguages } from "../../app/slices/TrainingLocalizationSlice";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import { TranslationWithDescriptionDTO } from "../../dto/TranslationWithDescriptionDTO";
import { getCourseTranslationStatus } from "./courseTranslationStatus";

/** Keep in step with the expand/collapse transition below. */
const EXPAND_MS = 150;

export default function CourseTranslationRow(props: {
  shortcode: string;
  lang: string;
  dto: TranslationWithDescriptionDTO;
  onChange: (dto: TranslationWithDescriptionDTO) => void;
  defaultOpen?: boolean;
}) {
  const { shortcode, lang, dto, onChange, defaultOpen } = { ...props };
  const { t } = useTranslation();
  // Collapsed unless this is the reference language - opening every language at
  // once is what made this list unscannable.
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen ?? false);
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<number>();

  useEffect(() => () => window.clearTimeout(scrollTimeout.current), []);

  const toggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (!opening) return;

    // Wait out the expand animation, otherwise we scroll to the collapsed
    // height and the description ends up below the fold.
    window.clearTimeout(scrollTimeout.current);
    scrollTimeout.current = window.setTimeout(() => {
      rootRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    }, EXPAND_MS + 60);
  };

  const availableLangs = useSelector(selectAvailableLanguages);
  const myLang = availableLangs.find((l) => l.shortcode === lang);
  const refLang = useSelector(selectReferenceLanguage);
  const isReference = refLang === lang;
  const status = getCourseTranslationStatus(dto);

  const onChangeName = (e: string) => {
    onChange({ ...dto, name: e });
  };
  const onChangeDesc = (e: string) => {
    const cdto = structuredClone(dto);
    cdto.description = e;
    onChange(cdto);
  };

  const statusLabel =
    status === "ready"
      ? t("publish_page.status_ready", { defaultValue: "Ready" })
      : status === "name_missing"
      ? t("publish_page.status_name_missing", { defaultValue: "Name missing" })
      : t("publish_page.status_not_translated", {
          defaultValue: "Not translated",
        });

  const header = (
    <div
      className="flex w-full cursor-pointer select-none items-center gap-[10px] px-[14px] py-[12px]"
      onClick={toggle}
      data-testid={`course-translation-row-${shortcode}`}
    >
      <div
        className={classNames("flex text-[16px] text-creator-text-soft", {
          "transition-transform rotate-0": isOpen,
          "transition-transform rotate-180": !isOpen,
        })}
      >
        <KeyboardArrowUpIcon color="inherit" fontSize="inherit" />
      </div>

      <div className="text-[13.5px] font-medium text-creator-text">
        {myLang?.full ?? shortcode}
      </div>
      <span className="rounded-[4px] bg-creator-childActive px-[6px] py-[2px] font-mono text-[10.5px] text-creator-text-sub">
        {shortcode}
      </span>
      {isReference && (
        <span className="rounded-[4px] bg-[var(--pi-key-fade)] px-[6px] py-[2px] text-[9.5px] font-semibold uppercase tracking-wide text-creator-text-key">
          {t("publish_page.reference_badge", { defaultValue: "Reference" })}
        </span>
      )}

      <div className="ml-auto flex items-center gap-[5px]">
        {status === "ready" ? (
          <>
            <span className="flex h-[13px] w-[13px] items-center justify-center text-[13px] leading-none text-[var(--pi-key-green)]">
              <CheckIcon color="inherit" fontSize="inherit" />
            </span>
            <span className="text-[11.5px] font-medium text-[var(--pi-key-green)]">
              {statusLabel}
            </span>
          </>
        ) : (
          <>
            <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--pi-key-yellow)]" />
            <span className="text-[11.5px] font-medium text-[var(--pi-key-yellow-text)]">
              {statusLabel}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const body = (
    <motion.div
      className="flex flex-col gap-[10px] overflow-hidden pb-[14px] pl-[38px] pr-[14px]"
      initial={{ height: "0px" }}
      animate={{ height: "auto", transition: { duration: EXPAND_MS / 1000 } }}
      exit={{ height: "0px", transition: { duration: EXPAND_MS / 1000 } }}
    >
      <PiInputfield
        value={dto.name}
        label={t("course_name")}
        placeholder={t("course_name_dotted")}
        onChange={onChangeName}
      />
      <PiTextArea
        value={dto.description}
        label={t("course_description")}
        placeholder={t("course_description_dotted")}
        onChange={onChangeDesc}
      />
    </motion.div>
  );

  return (
    <div
      ref={rootRef}
      className="w-full border-0 border-b-[1px] border-solid border-creator-border last:border-b-0"
    >
      {header}
      <AnimatePresence initial={false}>{isOpen && body}</AnimatePresence>
    </div>
  );
}
